import * as React from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  FilterSearch,
  PageHeader,
  Spinner,
  useToast,
} from "@chotca/ui";
import {
  Check,
  ChevronDown,
  ChevronRight,
  CloudUpload,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import {
  adminImportPostingRowSchema,
  describeViolations,
  detectContactViolations,
  isNegotiableSalary,
  SALARY_NEGOTIABLE_LABEL,
  type AdminImportPostingRow,
  type AdminImportPostingsResponse,
} from "@chotca/config";
import { getSdk, useAdminOrgs, useOrgAddresses } from "@chotca/api-client";

// A parsed row carries the import payload plus client-only review state.
type ReviewRow = {
  key: number;
  data: AdminImportPostingRow;
  aiOrg: AdminImportPostingRow["org"]; // original AI mapping, for revert
  orgLabel?: string; // display label after a manual org re-select
  include: boolean;
  expanded: boolean;
  // Admin saw the "nghi trùng" warning and chose to keep creating a new org.
  dismissedCollision: boolean;
};

// An existing org that shares a name with a `new_org` row — a possible mismatch.
type OrgCandidate = {
  id: string;
  name: string;
  isShadow: boolean;
  district?: string | null;
  city?: string | null;
};

// Case/space-insensitive name key. Mirrors the backend `normalize()` so the
// client flags the same collisions the server would have silently merged before.
function normalizeName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

// Below this AI match-confidence the org mapping is treated as "needs a look".
const REVIEW_CONFIDENCE = 0.8;

type RowValidation = {
  ok: boolean;
  schemaErrors: string[];
  contactViolations: string[];
};

const ORG_MODE_LABEL: Record<AdminImportPostingRow["org"]["mode"], string> = {
  existing: "Quán có sẵn",
  new_branch: "Thêm chi nhánh",
  new_org: "Quán mới",
};

const ORG_MODE_TONE: Record<
  AdminImportPostingRow["org"]["mode"],
  "mist" | "sun" | "lime-soft"
> = {
  existing: "mist",
  new_branch: "sun",
  new_org: "lime-soft",
};

const SALARY_TYPE_OPTIONS = [
  { value: "PER_HOUR", label: "Theo giờ" },
  { value: "PER_SHIFT", label: "Theo ca" },
  { value: "PER_DAY", label: "Theo ngày" },
  { value: "PER_MONTH", label: "Theo tháng" },
] as const;

function orgName(row: ReviewRow): string {
  if (row.orgLabel) return row.orgLabel;
  const o = row.data.org;
  if (o.mode === "new_org") return o.newOrg?.name ?? "(quán mới)";
  if (o.mode === "new_branch") return o.newAddress?.alias ?? "chi nhánh mới";
  return "(quán đã chọn)";
}

function validateRow(row: AdminImportPostingRow): RowValidation {
  const parsed = adminImportPostingRowSchema.safeParse(row);
  const schemaErrors = parsed.success
    ? []
    : parsed.error.issues.map(
        (i) => `${i.path.join(".") || "row"}: ${i.message}`,
      );
  const violations = [
    ...detectContactViolations(row.posting?.title ?? ""),
    ...detectContactViolations(row.posting?.description ?? ""),
  ];
  const contactViolations = violations.length
    ? [describeViolations(violations)]
    : [];
  return {
    ok: schemaErrors.length === 0 && contactViolations.length === 0,
    schemaErrors,
    contactViolations,
  };
}

function toInt(v: number | bigint | string | undefined): number {
  const n = typeof v === "bigint" ? Number(v) : Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function formatVnd(v: number | bigint | string): string {
  const n = toInt(v);
  if (n === 0) return "—";
  return new Intl.NumberFormat("vi-VN").format(n);
}

export function PostingsImportClient() {
  const { toast } = useToast();
  const [rows, setRows] = React.useState<ReviewRow[]>([]);
  const [fileName, setFileName] = React.useState<string | null>(null);
  const [parseError, setParseError] = React.useState<string | null>(null);
  const [result, setResult] =
    React.useState<AdminImportPostingsResponse | null>(null);

  const validations = React.useMemo(
    () => rows.map((r) => validateRow(r.data)),
    [rows],
  );

  // Existing orgs found in the system that share a name with a `new_org` row,
  // keyed by normalized name. Filled async by the hidden <CollisionProbe>s below.
  const [collisions, setCollisions] = React.useState<
    Record<string, OrgCandidate[]>
  >({});
  const setCollision = React.useCallback(
    (nameKey: string, orgs: OrgCandidate[]) =>
      setCollisions((prev) => ({ ...prev, [nameKey]: orgs })),
    [],
  );

  // Distinct names of rows still mapped to a brand-new org — one probe each.
  const distinctNewOrgNames = React.useMemo(() => {
    const byKey = new Map<string, string>();
    for (const r of rows) {
      const o = r.data.org;
      if (o.mode === "new_org" && o.newOrg?.name) {
        byKey.set(normalizeName(o.newOrg.name), o.newOrg.name);
      }
    }
    return [...byKey.entries()].map(([key, name]) => ({ key, name }));
  }, [rows]);

  // Per-row review signal: a name collision with an existing org, and/or a
  // low AI confidence. Only meaningful while the row is still a new org/branch
  // (mode=existing means the admin already resolved it).
  const reviews = React.useMemo(
    () =>
      rows.map((r) => {
        const o = r.data.org;
        const candidates =
          o.mode === "new_org" && o.newOrg?.name && !r.dismissedCollision
            ? (collisions[normalizeName(o.newOrg.name)] ?? [])
            : [];
        const conf = o.matchConfidence ?? r.data.confidence;
        const lowConfidence =
          o.mode !== "existing" &&
          typeof conf === "number" &&
          conf < REVIEW_CONFIDENCE;
        return {
          candidates,
          lowConfidence,
          needsReview: candidates.length > 0 || lowConfidence,
        };
      }),
    [rows, collisions],
  );

  const needsReviewCount = rows.reduce(
    (acc, r, i) => acc + (r.include && reviews[i]?.needsReview ? 1 : 0),
    0,
  );

  const includedValidCount = rows.reduce(
    (acc, r, i) => acc + (r.include && validations[i]?.ok ? 1 : 0),
    0,
  );

  function handleFile(file: File) {
    setParseError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const json = JSON.parse(String(reader.result));
        const list: unknown = Array.isArray(json) ? json : json?.rows;
        if (!Array.isArray(list)) {
          throw new Error('File phải là mảng row hoặc { "rows": [...] }');
        }
        setRows(
          list.map((data, i) => {
            const d = data as AdminImportPostingRow;
            return {
              key: i,
              data: d,
              aiOrg: d.org,
              include: true,
              expanded: false,
              dismissedCollision: false,
            };
          }),
        );
        setFileName(file.name);
      } catch (e) {
        setRows([]);
        setFileName(file.name);
        setParseError(e instanceof Error ? e.message : "JSON không hợp lệ");
      }
    };
    reader.readAsText(file);
  }

  function updateRow(key: number, patch: (r: ReviewRow) => ReviewRow) {
    setRows((prev) => prev.map((r) => (r.key === key ? patch(r) : r)));
  }

  function patchPosting(
    key: number,
    patch: Partial<AdminImportPostingRow["posting"]>,
  ) {
    updateRow(key, (r) => ({
      ...r,
      data: { ...r.data, posting: { ...r.data.posting, ...patch } },
    }));
  }

  function reassignToExisting(
    key: number,
    orgId: string,
    addressId: string,
    label: string,
  ) {
    updateRow(key, (r) => ({
      ...r,
      orgLabel: label,
      data: {
        ...r.data,
        org: {
          mode: "existing",
          orgId,
          addressId,
          matchConfidence: 1,
          matchReason: "Admin chọn tay",
        },
      },
    }));
  }

  function revertOrg(key: number) {
    updateRow(key, (r) => ({
      ...r,
      orgLabel: undefined,
      dismissedCollision: false,
      data: { ...r.data, org: r.aiOrg },
    }));
  }

  function dismissCollision(key: number) {
    updateRow(key, (r) => ({ ...r, dismissedCollision: true }));
  }

  const importMutation = useMutation({
    mutationFn: () => {
      const payload = rows
        .filter((r, i) => r.include && validations[i]?.ok)
        .map((r) => r.data);
      return getSdk().admin.importPostings({ rows: payload });
    },
    onSuccess: (res) => {
      setResult(res);
      toast({
        tone: res.failed > 0 ? "warning" : "success",
        title: `Tạo ${res.created} tin${
          res.skippedDuplicate ? `, bỏ qua ${res.skippedDuplicate} trùng` : ""
        }${res.failed ? `, lỗi ${res.failed}` : ""}`,
      });
    },
    onError: () => toast({ tone: "error", title: "Nhập thất bại, thử lại" }),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Nhập tin từ Facebook"
        description="Tải file mapped.json, duyệt từng dòng — đổi quán, chỉnh lương — rồi tạo tin hàng loạt."
        actions={
          rows.length > 0 ? (
            <Button
              onClick={() => importMutation.mutate()}
              loading={importMutation.isPending}
              disabled={includedValidCount === 0}
            >
              Tạo tin ({includedValidCount})
            </Button>
          ) : null
        }
      />

      {/* Upload zone */}
      <Card className="p-6">
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[var(--radius-sm)] border-2 border-dashed border-smoke bg-mist/40 px-6 py-10 text-center transition-colors hover:border-lime hover:bg-paper">
          <CloudUpload size={28} className="text-ash" strokeWidth={1.75} />
          <span className="text-sm font-semibold text-ink">
            {fileName ?? "Chọn file mapped.json"}
          </span>
          <span className="text-xs text-ash">
            Mảng row hoặc {`{ "rows": [...] }`} — theo schema admin-import
          </span>
          <input
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
          />
        </label>
        {parseError ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-danger">
            <TriangleAlert size={16} /> {parseError}
          </p>
        ) : null}
      </Card>

      {/* Hidden lookups: one per distinct new-org name → fills `collisions`. */}
      {distinctNewOrgNames.map((n) => (
        <CollisionProbe
          key={n.key}
          nameKey={n.key}
          name={n.name}
          onResult={setCollision}
        />
      ))}

      {rows.length > 0 ? (
        <>
          {/* Summary */}
          <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="text-ink">
                <b>{rows.length}</b> dòng
              </span>
              <span className="text-lime-dark">
                <b>{validations.filter((v) => v.ok).length}</b> hợp lệ
              </span>
              <span className="text-danger">
                <b>{validations.filter((v) => !v.ok).length}</b> lỗi
              </span>
              {needsReviewCount > 0 ? (
                <span className="text-ember">
                  <b>{needsReviewCount}</b> cần kiểm tra
                </span>
              ) : null}
              <span className="text-ash">
                sẽ tạo: <b className="text-ink">{includedValidCount}</b>
              </span>
            </div>
            <span className="text-xs text-ash">
              Tin tạo xong sẽ đăng (OPEN) ngay — duyệt kỹ trước khi bấm tạo.
            </span>
          </Card>

          {/* Review rows */}
          <div className="space-y-2">
            {rows.map((row, i) => {
              const v = validations[i]!;
              const rv = reviews[i]!;
              const p = row.data.posting;
              return (
                <Card
                  key={row.key}
                  className={`overflow-hidden ${
                    !v.ok
                      ? "border-danger/40"
                      : rv.needsReview
                        ? "border-ember/40"
                        : ""
                  }`}
                >
                  <div className="flex items-start gap-3 p-4">
                    <Checkbox
                      checked={row.include}
                      onChange={(e) =>
                        updateRow(row.key, (r) => ({
                          ...r,
                          include: e.target.checked,
                        }))
                      }
                    />
                    <button
                      type="button"
                      className="mt-0.5 text-ash"
                      onClick={() =>
                        updateRow(row.key, (r) => ({
                          ...r,
                          expanded: !r.expanded,
                        }))
                      }
                    >
                      {row.expanded ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </button>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge tone={ORG_MODE_TONE[row.data.org.mode]}>
                          {ORG_MODE_LABEL[row.data.org.mode]}
                        </Badge>
                        {rv.needsReview ? (
                          <Badge tone="ember">⚠ cần kiểm tra</Badge>
                        ) : null}
                        <span className="text-xs text-ash">{orgName(row)}</span>
                        <Badge tone="smoke">{p?.position}</Badge>
                        {typeof row.data.confidence === "number" ? (
                          <span
                            className={`text-xs ${
                              row.data.confidence < 0.7
                                ? "text-ember"
                                : "text-ash"
                            }`}
                          >
                            tin cậy {(row.data.confidence * 100).toFixed(0)}%
                          </span>
                        ) : null}
                        {row.data.sourceUrl ? (
                          <a
                            href={row.data.sourceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-lime-dark underline hover:text-ink"
                          >
                            Bài gốc ↗
                          </a>
                        ) : null}
                      </div>
                      <p className="mt-1 truncate font-semibold text-ink">
                        {p?.title || "(thiếu tiêu đề)"}
                      </p>
                      <p className="mt-0.5 text-xs text-ash">
                        {isNegotiableSalary(p?.salaryMax ?? 0)
                          ? `${SALARY_NEGOTIABLE_LABEL} · ${p?.employmentType} · ${p?.headcount} người`
                          : `${formatVnd(p?.salaryMin ?? 0)}–${formatVnd(
                              p?.salaryMax ?? 0,
                            )} ₫ · ${p?.salaryType} · ${p?.employmentType} · ${
                              p?.headcount
                            } người`}
                      </p>
                      {p?.benefits?.length ? (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {p.benefits.map((b) => (
                            <span
                              key={b}
                              className="rounded-full bg-mist px-2 py-0.5 text-[11px] text-ink/70"
                            >
                              {b}
                            </span>
                          ))}
                        </div>
                      ) : null}

                      {!v.ok ? (
                        <div className="mt-2 space-y-1">
                          {v.contactViolations.map((m, k) => (
                            <p
                              key={`c${k}`}
                              className="flex items-center gap-1.5 text-xs text-danger"
                            >
                              <TriangleAlert size={13} /> Lộ liên hệ: {m}
                            </p>
                          ))}
                          {v.schemaErrors.map((m, k) => (
                            <p key={`s${k}`} className="text-xs text-danger">
                              • {m}
                            </p>
                          ))}
                        </div>
                      ) : null}
                      {row.data.warnings?.length ? (
                        <div className="mt-1.5 space-y-0.5">
                          {row.data.warnings.map((w, k) => (
                            <p key={k} className="text-xs text-ember">
                              ⚠ {w}
                            </p>
                          ))}
                        </div>
                      ) : null}

                      {rv.candidates.length > 0 ? (
                        <CollisionPanel
                          name={
                            row.data.org.mode === "new_org"
                              ? (row.data.org.newOrg?.name ?? "")
                              : ""
                          }
                          sourceUrl={row.data.sourceUrl}
                          candidates={rv.candidates}
                          onMap={(orgId, addressId, label) =>
                            reassignToExisting(row.key, orgId, addressId, label)
                          }
                          onDismiss={() => dismissCollision(row.key)}
                        />
                      ) : null}
                    </div>
                  </div>

                  {row.expanded ? (
                    <RowEditor
                      row={row}
                      onPatchPosting={(patch) => patchPosting(row.key, patch)}
                      onReassign={(orgId, addressId, label) =>
                        reassignToExisting(row.key, orgId, addressId, label)
                      }
                      onRevertOrg={() => revertOrg(row.key)}
                    />
                  ) : null}
                </Card>
              );
            })}
          </div>
        </>
      ) : null}

      {result ? (
        <Card className="p-4">
          <h3 className="mb-2 text-sm font-bold text-ink">
            Kết quả (batch {result.batchId})
          </h3>
          <div className="mb-3 flex gap-4 text-sm">
            <span className="text-lime-dark">tạo {result.created}</span>
            <span className="text-ash">trùng {result.skippedDuplicate}</span>
            <span className="text-danger">lỗi {result.failed}</span>
          </div>
          <ul className="space-y-1 text-sm">
            {result.results.map((r) => (
              <li key={r.index} className="flex items-center gap-2">
                <span className="w-8 text-ash">#{r.index}</span>
                {r.status === "created" ? (
                  <>
                    <Badge tone="lime-soft">đã tạo</Badge>
                    {r.postingId ? (
                      <Link
                        to={`/postings/${r.postingId}`}
                        className="text-lime-dark underline"
                      >
                        mở tin
                      </Link>
                    ) : null}
                  </>
                ) : r.status === "skipped_duplicate" ? (
                  <Badge tone="smoke">trùng, bỏ qua</Badge>
                ) : (
                  <>
                    <Badge tone="ember">lỗi</Badge>
                    <span className="text-danger">{r.error}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {importMutation.isPending ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Expanded editor — org re-select, salary range, title, description.
// Rendered only when a row is expanded, so its org/address queries stay lazy.
// ────────────────────────────────────────────────────────────────────────────
function RowEditor({
  row,
  onPatchPosting,
  onReassign,
  onRevertOrg,
}: {
  row: ReviewRow;
  onPatchPosting: (patch: Partial<AdminImportPostingRow["posting"]>) => void;
  onReassign: (orgId: string, addressId: string, label: string) => void;
  onRevertOrg: () => void;
}) {
  const p = row.data.posting;
  const isManual = !!row.orgLabel;
  return (
    <div className="space-y-4 border-t border-smoke bg-mist/30 p-4">
      {/* Org re-select */}
      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <label className="text-xs font-semibold text-ink">Doanh nghiệp</label>
          {isManual ? (
            <button
              type="button"
              onClick={onRevertOrg}
              className="flex items-center gap-1 text-xs text-ash hover:text-ink"
            >
              <RotateCcw size={12} /> Dùng lại đề xuất AI
            </button>
          ) : null}
        </div>
        <p className="mb-2 text-sm text-ink">
          <Badge tone={ORG_MODE_TONE[row.data.org.mode]}>
            {ORG_MODE_LABEL[row.data.org.mode]}
          </Badge>{" "}
          <span className="text-ink">{orgName(row)}</span>
        </p>
        <OrgReassign onChoose={onReassign} />
      </div>

      {/* Salary range */}
      <div>
        <label className="mb-1 block text-xs font-semibold text-ink">
          Khoảng lương (VND) · 0 = thoả thuận
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={0}
            value={toInt(p?.salaryMin)}
            onChange={(e) =>
              onPatchPosting({ salaryMin: Math.max(0, Number(e.target.value)) })
            }
            className="h-10 w-36 rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-paper px-3 text-sm outline-none focus:border-lime"
          />
          <span className="text-ash">–</span>
          <input
            type="number"
            min={0}
            value={toInt(p?.salaryMax)}
            onChange={(e) =>
              onPatchPosting({ salaryMax: Math.max(0, Number(e.target.value)) })
            }
            className="h-10 w-36 rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-paper px-3 text-sm outline-none focus:border-lime"
          />
          <select
            value={p?.salaryType}
            onChange={(e) =>
              onPatchPosting({
                salaryType: e.target
                  .value as AdminImportPostingRow["posting"]["salaryType"],
              })
            }
            className="h-10 rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-paper px-3 text-sm outline-none focus:border-lime"
          >
            {SALARY_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        {toInt(p?.salaryMin) > toInt(p?.salaryMax) ? (
          <p className="mt-1 text-xs text-danger">
            Lương tối thiểu đang lớn hơn tối đa.
          </p>
        ) : null}
      </div>

      {/* Title */}
      <div>
        <label className="mb-1 block text-xs font-semibold text-ink">
          Tiêu đề (≤120)
        </label>
        <input
          value={p?.title ?? ""}
          maxLength={120}
          onChange={(e) => onPatchPosting({ title: e.target.value })}
          className="w-full rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-paper px-3 py-2 text-sm outline-none focus:border-lime"
        />
      </div>

      {/* Description */}
      <div>
        <label className="mb-1 block text-xs font-semibold text-ink">
          Mô tả (≤5000) — không để lọt SĐT/Zalo/link
        </label>
        <textarea
          value={p?.description ?? ""}
          maxLength={5000}
          rows={6}
          onChange={(e) => onPatchPosting({ description: e.target.value })}
          className="w-full rounded-[var(--radius-sm)] border-[1.5px] border-smoke bg-paper px-3 py-2 text-sm outline-none focus:border-lime"
        />
      </div>

      {row.data.contacts ? (
        <p className="text-xs text-ash">
          Liên hệ (gắn lên quán, không vào mô tả):{" "}
          {[
            row.data.contacts.phone,
            row.data.contacts.zalo && `Zalo ${row.data.contacts.zalo}`,
            row.data.contacts.email,
          ]
            .filter(Boolean)
            .join(" · ") || "—"}
        </p>
      ) : null}
      {row.data.sourceUrl ? (
        <a
          href={row.data.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-lime-dark underline"
        >
          Xem bài gốc trên Facebook
        </a>
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CollisionProbe — invisible. Looks up existing orgs sharing a new_org's name
// and reports the exact-name matches up to the parent. One mounted per distinct
// name so each useAdminOrgs hook stays stable across renders.
// ────────────────────────────────────────────────────────────────────────────
function CollisionProbe({
  nameKey,
  name,
  onResult,
}: {
  nameKey: string;
  name: string;
  onResult: (nameKey: string, orgs: OrgCandidate[]) => void;
}) {
  const orgsQ = useAdminOrgs({ search: name });
  const items = orgsQ.data?.items;
  React.useEffect(() => {
    if (!items) return;
    const matches = items
      .filter((o) => normalizeName(o.name) === nameKey)
      .map((o) => ({
        id: o.id,
        name: o.name,
        isShadow: !!o.isShadow,
        district: o.primaryAddress?.district,
        city: o.primaryAddress?.city,
      }));
    onResult(nameKey, matches);
  }, [items, nameKey, onResult]);
  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// CollisionPanel — shown inline on a new_org row when an org with the same name
// already exists. Admin either maps the row onto one of those orgs' branches
// (→ mode=existing) or confirms it really is a new venue.
// ────────────────────────────────────────────────────────────────────────────
function CollisionPanel({
  name,
  sourceUrl,
  candidates,
  onMap,
  onDismiss,
}: {
  name: string;
  sourceUrl?: string;
  candidates: OrgCandidate[];
  onMap: (orgId: string, addressId: string, label: string) => void;
  onDismiss: () => void;
}) {
  const [picked, setPicked] = React.useState<OrgCandidate | null>(null);
  const addressesQ = useOrgAddresses(picked?.id ?? null);
  const addresses = addressesQ.data?.items ?? [];

  return (
    <div className="mt-2 rounded-[var(--radius-sm)] border border-ember/40 bg-sun/30 p-3">
      {!picked ? (
        <>
          <p className="flex items-center gap-1.5 text-xs font-semibold text-ember">
            <TriangleAlert size={13} /> Đã có {candidates.length} quán tên “
            {name}” trong hệ thống — kiểm tra có phải cùng quán không.
          </p>
          {sourceUrl ? (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-xs text-lime-dark underline hover:text-ink"
            >
              Mở bài gốc để đối chiếu ↗
            </a>
          ) : null}
          <ul className="mt-2 space-y-1">
            {candidates.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setPicked(c)}
                  className="flex w-full items-center justify-between gap-2 rounded border border-smoke bg-paper px-3 py-2 text-left text-sm hover:border-lime hover:bg-mist"
                >
                  <span className="min-w-0">
                    <span className="font-medium text-ink">{c.name}</span>
                    {c.isShadow ? (
                      <span className="ml-1 text-xs text-ash">(tạm)</span>
                    ) : null}
                    {c.district || c.city ? (
                      <span className="block truncate text-xs text-ink/55">
                        {[c.district, c.city].filter(Boolean).join(", ")}
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-xs text-lime-dark">
                    Cùng quán này →
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-2 text-xs text-ash underline hover:text-ink"
          >
            Khác quán — giữ tạo mới
          </button>
        </>
      ) : (
        <>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-medium text-ink">
              {picked.name} — chọn chi nhánh
            </span>
            <button
              type="button"
              onClick={() => setPicked(null)}
              className="text-xs text-ash hover:text-ink"
            >
              Quay lại
            </button>
          </div>
          {addressesQ.isLoading ? (
            <div className="flex justify-center py-3">
              <Spinner size="sm" />
            </div>
          ) : addresses.length === 0 ? (
            <p className="py-2 text-xs text-ash">Quán này chưa có chi nhánh.</p>
          ) : (
            <ul className="space-y-1">
              {addresses.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() =>
                      onMap(picked.id, a.id, `${picked.name} · ${a.alias}`)
                    }
                    className="flex w-full items-center justify-between gap-2 rounded border border-smoke bg-paper px-3 py-2 text-left text-sm hover:border-lime hover:bg-mist"
                  >
                    <span className="min-w-0">
                      <span className="font-medium text-ink">{a.alias}</span>
                      <span className="block truncate text-xs text-ink/55">
                        {[a.address, a.district, a.city]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </span>
                    <Check size={15} className="shrink-0 text-ash" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// OrgReassign — search an existing org, pick one of its branches → mode=existing
// ────────────────────────────────────────────────────────────────────────────
function OrgReassign({
  onChoose,
}: {
  onChoose: (orgId: string, addressId: string, label: string) => void;
}) {
  const [term, setTerm] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const [pickedOrg, setPickedOrg] = React.useState<{
    id: string;
    name: string;
  } | null>(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  const orgsQ = useAdminOrgs(debounced ? { search: debounced } : {});
  const addressesQ = useOrgAddresses(pickedOrg?.id ?? null);
  const orgs = orgsQ.data?.items ?? [];
  const addresses = addressesQ.data?.items ?? [];

  return (
    <div className="rounded-[var(--radius-sm)] border border-smoke bg-paper p-3">
      <p className="mb-2 text-xs text-ash">
        Đổi sang quán có sẵn (gõ tên để tìm):
      </p>
      <FilterSearch
        value={term}
        onChange={setTerm}
        placeholder="Tìm doanh nghiệp..."
        width="w-full"
      />

      {!pickedOrg ? (
        <div className="mt-2 max-h-48 overflow-y-auto">
          {orgsQ.isFetching && orgs.length === 0 ? (
            <div className="flex justify-center py-4">
              <Spinner size="sm" />
            </div>
          ) : debounced && orgs.length === 0 ? (
            <p className="py-3 text-center text-xs text-ash">
              Không tìm thấy quán khớp “{debounced}”
            </p>
          ) : (
            <ul>
              {orgs.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => setPickedOrg({ id: o.id, name: o.name })}
                    className="flex w-full items-start justify-between gap-2 rounded px-2 py-2 text-left text-sm hover:bg-mist"
                  >
                    <span className="min-w-0">
                      <span className="font-medium text-ink">{o.name}</span>
                      {o.isShadow ? (
                        <span className="ml-1 text-xs text-ash">(tạm)</span>
                      ) : null}
                      {o.primaryAddress ? (
                        <span className="block truncate text-xs text-ink/55">
                          {[o.primaryAddress.district, o.primaryAddress.city]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="mt-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-sm font-medium text-ink">
              {pickedOrg.name}
            </span>
            <button
              type="button"
              onClick={() => setPickedOrg(null)}
              className="text-xs text-ash hover:text-ink"
            >
              Đổi quán khác
            </button>
          </div>
          <p className="mb-1 text-xs text-ash">Chọn chi nhánh:</p>
          {addressesQ.isLoading ? (
            <div className="flex justify-center py-3">
              <Spinner size="sm" />
            </div>
          ) : addresses.length === 0 ? (
            <p className="py-2 text-xs text-ash">Quán này chưa có chi nhánh.</p>
          ) : (
            <ul className="space-y-1">
              {addresses.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() =>
                      onChoose(
                        pickedOrg.id,
                        a.id,
                        `${pickedOrg.name} · ${a.alias}`,
                      )
                    }
                    className="flex w-full items-center justify-between gap-2 rounded border border-smoke px-3 py-2 text-left text-sm hover:border-lime hover:bg-mist"
                  >
                    <span className="min-w-0">
                      <span className="font-medium text-ink">{a.alias}</span>
                      <span className="block truncate text-xs text-ink/55">
                        {[a.address, a.district, a.city]
                          .filter(Boolean)
                          .join(", ")}
                      </span>
                    </span>
                    <Check size={15} className="shrink-0 text-ash" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
